// components/events/ExpensesPanel.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card, Row, Col, Form, Button, Table, Spinner, Alert, Badge, Modal
} from "react-bootstrap";
import { db } from "../../firebase";
import {
  collection, query, orderBy, onSnapshot,
  addDoc, deleteDoc, doc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { fmt, fmtTs, cleanObject } from "../../utils/eventUtils";

/**
 * Props:
 *  - communityId: string
 *  - event: Firestore event doc { id, committee, committeeUids, ... }
 *  - residents: [{ uid, fullName|displayName, unit: { blockName, flatLabel }, ... }]
 *  - canEditExpenses: boolean (Admin/Manager/Committee depending on your gating)
 */
export default function ExpensesPanel({ communityId, event, residents, canEditExpenses }) {
  const eventId = event?.id;

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    purpose: "",
    category: "",     // ✅ NEW: category
    amount: "",
    paidByUid: "",
    paidByName: "",
    receiptNo: "",
  });

  // Edit modal state
  const [showEdit, setShowEdit] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editExpense, setEditExpense] = useState(null); // the selected row
  const [editForm, setEditForm] = useState({
    purpose: "",
    category: "",
    amount: "",
    paidByUid: "",
    paidByName: "",
    receiptNo: "",
  });

  const totalExpenses = useMemo(
    () => expenses.reduce((acc, e) => acc + (Number(e.amount) || 0), 0),
    [expenses]
  );

  /* ---------------------------------
     Live: expenses
  ---------------------------------- */
  useEffect(() => {
    if (!eventId) {
      setError("Missing eventId in URL.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    const qExp = query(collection(db, "events", eventId, "expenses"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      qExp,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setExpenses(rows);
        setLoading(false);
      },
      (err) => {
        console.error("Expenses snapshot error:", err);
        setError(err?.message || "Failed to load expenses.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [eventId]);

  /* ---------------------------------
     Helpers: Resident label by UID
  ---------------------------------- */
  const residentLabelByUid = useCallback(
    (uid) => {
      if (!uid) return "";
      const u = (Array.isArray(residents) ? residents : []).find((x) => x.uid === uid);
      if (!u) return uid;
      const name = u.fullName || u.displayName || uid;
      const b = u.unit?.blockName || "";
      const f = u.unit?.flatLabel || "";
      return [name, b && f ? `(${b}-${f})` : ""].filter(Boolean).join(" ");
    },
    [residents]
  );

  /* ---------------------------------
     Committee options (UID + custom name)
  ---------------------------------- */
  const committeeUidOptions = useMemo(
    () =>
      (Array.isArray(event?.committee) ? event.committee : [])
        .filter((m) => m?.uid)
        .map((m) => ({
          uid: m.uid,
          label: residentLabelByUid(m.uid) || m.uid,
        })),
    [event?.committee, residentLabelByUid]
  );

  const committeeNameOptions = useMemo(
    () =>
      (Array.isArray(event?.committee) ? event.committee : [])
        .filter((m) => !m?.uid && (m?.residentName || "").trim())
        .map((m) => ({
          name: (m.residentName || "").trim(),
        })),
    [event?.committee]
  );

  /* ---------------------------------
     Add expense
  ---------------------------------- */
  const addExpense = async () => {
    if (!canEditExpenses) return alert("Not allowed for your role.");
    if (!eventId) return alert("Missing eventId; check the URL.");

    const purpose = String(form.purpose || "").trim();
    const category = String(form.category || "").trim(); // ✅ NEW
    const amt = Number(form.amount);

    if (!purpose) return alert("Purpose is required.");
    if (!category) return alert("Category is required."); // ✅ NEW validation
    if (!(amt > 0)) return alert("Amount must be > 0.");

    const payload = cleanObject({
      communityId: String(communityId || ""),
      eventId: String(eventId || ""),
      purpose,
      category, // ✅ NEW: persisted in Firestore
      amount: amt,
      // prefer UID, but keep name for readability
      paidByUid: typeof form.paidByUid === "string" && form.paidByUid ? form.paidByUid : undefined,
      paidByName: (form.paidByName || "").trim(),
      receiptNo: (form.receiptNo || "").trim(),
      createdAt: serverTimestamp(),
    });

    try {
      setAdding(true);
      await addDoc(collection(db, "events", eventId, "expenses"), payload);
      setForm({
        purpose: "",
        category: "",   // ✅ clear
        amount: "",
        paidByUid: "",
        paidByName: "",
        receiptNo: "",
      });
    } catch (err) {
      console.error("Add expense error:", err);
      alert(err?.message || "Failed to add expense.");
    } finally {
      setAdding(false);
    }
  };

  /* ---------------------------------
     Edit expense (open modal, save changes)
  ---------------------------------- */
  const openEdit = (exp) => {
    setEditExpense(exp);
    setEditForm({
      purpose: exp.purpose || "",
      category: exp.category || "", // may be empty in older rows
      amount: String(exp.amount ?? ""),
      paidByUid: exp.paidByUid || "",
      paidByName: exp.paidByName || "",
      receiptNo: exp.receiptNo || "",
    });
    setShowEdit(true);
  };

  const closeEdit = () => {
    setShowEdit(false);
    setEditExpense(null);
    setSavingEdit(false);
  };

  const saveEdit = async () => {
    if (!canEditExpenses) return alert("Not allowed for your role.");
    if (!eventId || !editExpense?.id) return alert("Invalid expense selected.");

    const purpose = String(editForm.purpose || "").trim();
    const category = String(editForm.category || "").trim();
    const amt = Number(editForm.amount);

    if (!purpose) return alert("Purpose is required.");
    if (!category) return alert("Category is required.");
    if (!(amt > 0)) return alert("Amount must be > 0.");

    // When UID is selected, keep it; otherwise rely on name.
    const payload = cleanObject({
      purpose,
      category,
      amount: amt,
      paidByUid: typeof editForm.paidByUid === "string" && editForm.paidByUid ? editForm.paidByUid : undefined,
      paidByName: (editForm.paidByName || "").trim(),
      receiptNo: (editForm.receiptNo || "").trim(),
      // Optional: track updatedAt to know edits later
      updatedAt: serverTimestamp(),
    });

    try {
      setSavingEdit(true);
      await updateDoc(doc(db, "events", eventId, "expenses", editExpense.id), payload);
      closeEdit();
    } catch (err) {
      console.error("Update expense error:", err);
      alert(err?.message || "Failed to update expense.");
      setSavingEdit(false);
    }
  };

  const deleteExpense = async (id) => {
    if (!canEditExpenses) return alert("Not allowed for your role.");
    if (!eventId) return alert("Missing eventId; check the URL.");
    if (!window.confirm("Delete this expense?")) return;
    try {
      await deleteDoc(doc(db, "events", eventId, "expenses", id));
    } catch (err) {
      console.error("Delete expense error:", err);
      alert(err?.message || "Failed to delete expense.");
    }
  };

  /* ---------------------------------
     Render
  ---------------------------------- */
  return (
    <div>
      <div className="d-flex align-items-center justify-content-between">
        <h6 className="mb-3">Expenses</h6>
        <div>
          <Badge bg="danger" className="me-2">Total Expenses: ₹ {fmt(totalExpenses)}</Badge>
        </div>
      </div>

      {canEditExpenses && (
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-2">
              {/* Purpose */}
              <Col md={4}>
                <Form.Label>Purpose</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Stage, Decorations"
                  value={form.purpose}
                  onChange={(e) => setForm((s) => ({ ...s, purpose: e.target.value }))}
                />
              </Col>

              {/* Category (NEW) */}
              <Col md={3}>
                <Form.Label>Category</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Catering, Games, Decorations"
                  value={form.category}
                  onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))}
                />
                {/* If you prefer preset choices, replace the input above with:
                <Form.Select
                  value={form.category}
                  onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))}
                >
                  <option value="">— Select category —</option>
                  <option value="Catering">Catering</option>
                  <option value="Games">Games</option>
                  <option value="Decorations">Decorations</option>
                  <option value="Logistics">Logistics</option>
                  <option value="Gifts">Gifts</option>
                  <option value="Misc">Misc</option>
                </Form.Select>
                */}
              </Col>

              {/* Amount */}
              <Col md={2}>
                <Form.Label>Amount (₹)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm((s) => ({ ...s, amount: e.target.value }))}
                />
              </Col>

              {/* Paid By (Committee) — UID or custom name */}
              <Col md={3}>
                <Form.Label>Paid By (Committee)</Form.Label>
                <Form.Select
                  value={
                    form.paidByUid
                      ? `uid:${form.paidByUid}`
                      : form.paidByName
                      ? `name:${form.paidByName}`
                      : ""
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.startsWith("uid:")) {
                      const uid = val.slice(4);
                      const resolvedName = residentLabelByUid(uid) || "";
                      setForm((s) => ({ ...s, paidByUid: uid, paidByName: resolvedName }));
                    } else if (val.startsWith("name:")) {
                      const name = val.slice(5);
                      setForm((s) => ({ ...s, paidByUid: "", paidByName: name }));
                    } else {
                      setForm((s) => ({ ...s, paidByUid: "", paidByName: "" }));
                    }
                  }}
                >
                  <option value="">— Select committee member —</option>

                  {/* Registered (UID) */}
                  {committeeUidOptions.map((opt, idx) => (
                    <option key={`exp-committee-uid-${opt.uid}-${idx}`} value={`uid:${opt.uid}`}>
                      {opt.label}
                    </option>
                  ))}

                  {/* Custom names */}
                  {committeeNameOptions.map((opt, idx) => (
                    <option key={`exp-committee-name-${opt.name}-${idx}`} value={`name:${opt.name}`}>
                      {opt.name}
                    </option>
                  ))}
                </Form.Select>
                <div className="form-text">
                  Pick a registered member or a custom name. You can also override below.
                </div>
              </Col>

              {/* Paid By (Name) — manual override or non-committee payer */}
              <Col md={3}>
                <Form.Label>Paid By (Name)</Form.Label>
                <Form.Control
                  value={form.paidByName}
                  onChange={(e) => setForm((s) => ({ ...s, paidByName: e.target.value }))}
                  placeholder="optional (if not in committee)"
                />
              </Col>

              {/* Receipt No */}
              <Col md={2}>
                <Form.Label>Receipt No</Form.Label>
                <Form.Control
                  value={form.receiptNo}
                  onChange={(e) => setForm((s) => ({ ...s, receiptNo: e.target.value }))}
                />
              </Col>
            </Row>

            <div className="mt-3">
              <Button
                variant="primary"
                onClick={addExpense}
                disabled={adding || !form.purpose || !form.category || !form.amount}
              >
                {adding ? "Saving…" : "Add Expense"}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? (
        <div className="d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Loading expenses…</span>
        </div>
      ) : (
        <Table bordered hover responsive size="sm">
          <thead>
            <tr>
              <th>Purpose</th>
              <th>Category</th> {/* ✅ NEW column */}
              <th>Amount (₹)</th>
              <th>Paid By</th>
              <th>Receipt</th>
              <th>Created</th>
              {canEditExpenses && <th style={{ width: 160 }}>Actions</th>} {/* widened for 2 buttons */}
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 ? (
              <tr>
                <td colSpan={canEditExpenses ? 7 : 6} className="text-center">
                  No expenses yet
                </td>
              </tr>
            ) : (
              expenses.map((e) => {
                const paidBy =
                  e.paidByUid
                    ? (residentLabelByUid(e.paidByUid) || e.paidByUid)
                    : (e.paidByName || "—");

                return (
                  <tr key={e.id}>
                    <td>{e.purpose || "—"}</td>
                    <td>{e.category || "—"}</td> {/* ✅ renders even for old rows */}
                    <td>₹ {fmt(e.amount)}</td>
                    <td>{paidBy}</td>
                    <td>{e.receiptNo || "—"}</td>
                    <td>{fmtTs(e.createdAt)}</td>
                    {canEditExpenses && (
                      <td className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => openEdit(e)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => deleteExpense(e.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      )}

      {/* Edit Modal */}
      <Modal show={showEdit} onHide={closeEdit} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Expense</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editExpense ? (
            <Row className="g-2">
              {/* Purpose */}
              <Col md={6}>
                <Form.Label>Purpose</Form.Label>
                <Form.Control
                  type="text"
                  value={editForm.purpose}
                  onChange={(e) => setEditForm((s) => ({ ...s, purpose: e.target.value }))}
                />
              </Col>

              {/* Category */}
              <Col md={6}>
                <Form.Label>Category</Form.Label>
                <Form.Control
                  type="text"
                  value={editForm.category}
                  onChange={(e) => setEditForm((s) => ({ ...s, category: e.target.value }))}
                />
              </Col>

              {/* Amount */}
              <Col md={4}>
                <Form.Label>Amount (₹)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.amount}
                  onChange={(e) => setEditForm((s) => ({ ...s, amount: e.target.value }))}
                />
              </Col>

              {/* Paid By (Committee) — UID or custom name */}
              <Col md={8}>
                <Form.Label>Paid By (Committee)</Form.Label>
                <Form.Select
                  value={
                    editForm.paidByUid
                      ? `uid:${editForm.paidByUid}`
                      : editForm.paidByName
                      ? `name:${editForm.paidByName}`
                      : ""
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.startsWith("uid:")) {
                      const uid = val.slice(4);
                      const resolvedName = residentLabelByUid(uid) || "";
                      setEditForm((s) => ({ ...s, paidByUid: uid, paidByName: resolvedName }));
                    } else if (val.startsWith("name:")) {
                      const name = val.slice(5);
                      setEditForm((s) => ({ ...s, paidByUid: "", paidByName: name }));
                    } else {
                      setEditForm((s) => ({ ...s, paidByUid: "", paidByName: "" }));
                    }
                  }}
                >
                  <option value="">— Select committee member —</option>

                  {/* Registered (UID) */}
                  {committeeUidOptions.map((opt, idx) => (
                    <option key={`edit-committee-uid-${opt.uid}-${idx}`} value={`uid:${opt.uid}`}>
                      {opt.label}
                    </option>
                  ))}

                  {/* Custom names */}
                  {committeeNameOptions.map((opt, idx) => (
                    <option key={`edit-committee-name-${opt.name}-${idx}`} value={`name:${opt.name}`}>
                      {opt.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {/* Paid By (Name) — manual override */}
              <Col md={8}>
                <Form.Label>Paid By (Name)</Form.Label>
                <Form.Control
                  value={editForm.paidByName}
                  onChange={(e) => setEditForm((s) => ({ ...s, paidByName: e.target.value }))}
                  placeholder="optional (if not in committee)"
                />
              </Col>

              {/* Receipt No */}
              <Col md={4}>
                <Form.Label>Receipt No</Form.Label>
                <Form.Control
                  value={editForm.receiptNo}
                  onChange={(e) => setEditForm((s) => ({ ...s, receiptNo: e.target.value }))}
                />
              </Col>
            </Row>
          ) : (
            <Alert variant="warning">No expense selected.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit} disabled={savingEdit}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={saveEdit}
            disabled={savingEdit || !editForm.purpose || !editForm.category || !editForm.amount}
          >
            {savingEdit ? "Saving…" : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
